import { useMemo, useContext, useState, useEffect } from 'react';
import {
	Alert,
	Box,
	Button,
	Typography,
	IconButton,
	useTheme,
	useMediaQuery,
	alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Sidebar, {
	drawerWidthExpanded,
	drawerWidthCollapsed,
	navMargin,
} from './Sidebar';
import ChangePasswordModal from './Modals/ChangePasswordModal';
import ChoosePlanModal from './Modals/ChoosePlanModal';
import OnboardingChecklist from './OnboardingChecklist';
import ProOnboardingChecklist from './ProOnboardingChecklist';
import OrgAvatar from './OrgAvatar';
import AmbientBackground from './AmbientBackground';
import PageContent from './PageContent';
import { glassSurface } from '../Shared/glassStyles';
import { AppContext, ACTION_TYPES } from '../Context/AppContext';
import {
	completeConversionCheckout,
	makeApiRequest,
} from '../Shared/Api';
import { registerChoosePlanOpener, trialDaysLeft, trialDaysLeftLabel } from '../Shared/trial';

const Layout = ({ children, toggleDarkMode, darkMode }) => {
	const theme = useTheme();
	const location = useLocation();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [mobileOpen, setMobileOpen] = useState(false);
	const [choosePlanOpen, setChoosePlanOpen] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(() => {
		const saved = localStorage.getItem('sidebarOpen');
		return saved !== null ? JSON.parse(saved) : true;
	});
	const { state, dispatch } = useContext(AppContext);

	const title = useMemo(() => state.pageTitle, [state.pageTitle]);
	// Key off pathname (not page title) so in-page title changes — e.g. mobile
	// Settings sections — do not remount children into a loading loop.
	const contentKey = `${state.user.organization?.id ?? 'default'}-${location.pathname}`;

	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const organization = state.user.organization;
	const isPortfolioDemo = Boolean(organization?.is_portfolio_demo);
	const trialActive = Boolean(organization?.trial_active);
	const requiresPlanSelection = Boolean(organization?.requires_plan_selection);
	const daysLeft = trialDaysLeft(organization?.trial_ends_at);
	const daysLeftLabel = trialDaysLeftLabel(daysLeft);

	useEffect(() => registerChoosePlanOpener(() => setChoosePlanOpen(true)), []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const converted = params.get('converted');
		const sessionId = params.get('session_id');
		if (converted !== '1' && converted !== '0') {
			return undefined;
		}
		let cancelled = false;
		const finish = async () => {
			try {
				if (converted === '1' && sessionId) {
					await completeConversionCheckout(sessionId);
				}
				const userData = await makeApiRequest('GET', '/auth/me/');
				if (cancelled) {
					return;
				}
				dispatch({ type: ACTION_TYPES.UPDATE_USER, payload: userData });
				if (converted === '1') {
					enqueueSnackbar('Your plan is active. Welcome aboard.', {
						variant: 'success',
					});
				}
			} catch (error) {
				if (converted === '1') {
					enqueueSnackbar(
						error?.data?.error ||
							'Billing is still confirming. Refresh in a moment.',
						{ variant: 'info' },
					);
				}
			} finally {
				params.delete('converted');
				params.delete('session_id');
				const next = params.toString();
				navigate(
					{ pathname: location.pathname, search: next ? `?${next}` : '' },
					{ replace: true },
				);
			}
		};
		finish();
		return () => {
			cancelled = true;
		};
	}, [
		dispatch,
		enqueueSnackbar,
		location.pathname,
		location.search,
		navigate,
	]);

	const sidebarWidth = isMobile
		? 0
		: sidebarOpen
			? drawerWidthExpanded
			: drawerWidthCollapsed;

	const mainOffset = isMobile ? 0 : sidebarWidth + navMargin * 2;

	return (
		<Box
			sx={{
				position: 'relative',
				minHeight: '100vh',
				overflow: 'hidden',
			}}
		>
			<AmbientBackground />

			<ChangePasswordModal open={Boolean(state.user.needsPassword)} />
			<ChoosePlanModal
				open={requiresPlanSelection || choosePlanOpen}
				required={requiresPlanSelection}
				onClose={() => setChoosePlanOpen(false)}
			/>

			{state.initialized && isManager && !requiresPlanSelection && (
				<Box
					sx={{
						position: 'fixed',
						zIndex: theme.zIndex.modal - 1,
						right: { xs: 16, sm: 24 },
						bottom: { xs: 80, sm: 24 },
						display: 'flex',
						flexDirection: 'column',
						gap: 1.5,
						alignItems: 'flex-end',
						pointerEvents: 'none',
						'& > *': { pointerEvents: 'auto' },
					}}
				>
					<ProOnboardingChecklist />
					<OnboardingChecklist />
				</Box>
			)}

			<Sidebar
				mobileOpen={mobileOpen}
				onMobileClose={() => setMobileOpen(false)}
				toggleDarkMode={toggleDarkMode}
				darkMode={darkMode}
				sidebarOpen={sidebarOpen}
				onSidebarOpenChange={setSidebarOpen}
			/>

			<Box
				component="main"
				sx={{
					position: 'relative',
					zIndex: 1,
					ml: { xs: 0, md: `${mainOffset}px` },
					minHeight: '100vh',
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					transition: theme.transitions.create('margin-left', {
						easing: theme.transitions.easing.easeInOut,
						duration: theme.transitions.duration.enteringScreen,
					}),
				}}
			>
				{isPortfolioDemo && (
					<Alert
						severity="info"
						sx={{
							flexShrink: 0,
							borderRadius: 0,
							py: 0.5,
							backdropFilter: 'blur(12px)',
							'& .MuiAlert-message': { width: '100%', textAlign: 'center' },
						}}
					>
						Demo environment — sample data resets weekly. Soft edits are fine;
						uploads, seats, email, SMS, and Stripe Connect are disabled here.
					</Alert>
				)}
				{trialActive && !isPortfolioDemo && (
					<Alert
						severity="warning"
						action={
							isManager ? (
								<Button
									color="inherit"
									size="small"
									onClick={() => setChoosePlanOpen(true)}
								>
									Upgrade
								</Button>
							) : null
						}
						sx={{
							flexShrink: 0,
							borderRadius: 0,
							py: 0.25,
							'& .MuiAlert-message': { width: '100%' },
						}}
					>
						Free trial
						{daysLeftLabel ? ` — ${daysLeftLabel}` : ''}
						. No credit card on file.
					</Alert>
				)}

				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
						overflowX: 'hidden',
					}}
				>
					{/* Floating glass header — sticky within the scroll container */}
					<Box
						sx={{
							position: 'sticky',
							top: 0,
							zIndex: theme.zIndex.appBar,
							flexShrink: 0,
							px: { xs: 1.5, sm: 2, md: 0 },
							pt: { xs: 1.5, md: `${navMargin}px` },
							pb: { xs: 1, md: 1.5 },
							mx: { xs: 0, md: `${navMargin}px` },
							mr: { xs: 0, md: `${navMargin}px` },
							background: (t) =>
								`linear-gradient(180deg, ${alpha(t.palette.background.default, 0.92)} 40%, ${alpha(t.palette.background.default, 0)} 100%)`,
							backdropFilter: 'blur(8px)',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 2,
								px: { xs: 2, sm: 2.5 },
								py: { xs: 1.25, sm: 1.5 },
								borderRadius: '20px',
								minHeight: 56,
								...glassSurface(theme, 'medium'),
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									minWidth: 0,
									flex: 1,
									gap: 1.5,
								}}
							>
								{isMobile && (
									<IconButton
										color="inherit"
										aria-label="Open navigation"
										onClick={() => setMobileOpen(true)}
										sx={{
											flexShrink: 0,
											borderRadius: '12px',
											color: 'text.secondary',
										}}
									>
										<MenuIcon />
									</IconButton>
								)}
								<Typography
									variant="h5"
									noWrap
									sx={{
										fontWeight: 700,
										color: 'text.primary',
										fontSize: { xs: '1.2rem', sm: '1.4rem' },
										letterSpacing: '-0.03em',
									}}
								>
									{title}
								</Typography>
							</Box>

							{organization?.name && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										px: { xs: 1.25, sm: 1.5 },
										py: 0.75,
										borderRadius: '14px',
										flexShrink: 0,
										maxWidth: { xs: 140, sm: 260 },
										background: alpha(theme.palette.primary.main, 0.06),
										border: '1px solid',
										borderColor: alpha(theme.palette.primary.main, 0.12),
									}}
								>
									<OrgAvatar organization={organization} size={26} />
									<Box
										sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}
									>
										<Typography
											variant="caption"
											sx={{
												display: 'block',
												color: 'text.secondary',
												fontSize: '0.6rem',
												fontWeight: 600,
												textTransform: 'uppercase',
												letterSpacing: '0.08em',
												lineHeight: 1.2,
											}}
										>
											Organization
										</Typography>
										<Typography
											variant="body2"
											noWrap
											sx={{
												fontWeight: 600,
												lineHeight: 1.3,
												fontSize: '0.85rem',
											}}
										>
											{organization.name}
										</Typography>
									</Box>
								</Box>
							)}
						</Box>
					</Box>

					<Box
						sx={{
							px: { xs: 2, sm: 3, md: 3 },
							pb: { xs: 3, md: 4 },
							pt: { xs: 1, md: 1 },
							mr: { xs: 0, md: `${navMargin}px` },
						}}
					>
						{requiresPlanSelection ? null : (
							<PageContent contentKey={contentKey}>{children}</PageContent>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default Layout;
